// Offline Cache Service for Security QR Scanning
// Uses IndexedDB for persistent storage

const DB_NAME = 'SecurityOfflineDB';
const DB_VERSION = 1;
const VISITORS_STORE = 'visitors';
const PENDING_SCANS_STORE = 'pendingScans';

// Production-safe logging
const isDev = process.env.NODE_ENV === 'development';
const log = (...args) => isDev && console.log(...args);
const logError = (...args) => isDev && console.error(...args);

// Token storage helper (mirrors AuthContext - sessionStorage only)
const getToken = () => {
  try {
    return sessionStorage.getItem('qr_access_token');
  } catch {
    return null;
  }
};

class OfflineCacheService {
  constructor() {
    this.db = null;
    this.isOnline = navigator.onLine;
    this.initPromise = this.initDB();
    
    // Listen for online/offline events
    window.addEventListener('online', () => {
      this.isOnline = true;
      this.syncPendingScans();
    });
    
    window.addEventListener('offline', () => {
      this.isOnline = false;
    });
  }

  async initDB() {
    return new Promise((resolve, reject) => {
      const request = indexedDB.open(DB_NAME, DB_VERSION);
      
      request.onerror = () => {
        logError('IndexedDB error:', request.error);
        reject(request.error);
      };
      
      request.onsuccess = () => {
        this.db = request.result;
        log('IndexedDB initialized successfully');
        resolve(this.db);
      };
      
      request.onupgradeneeded = (event) => {
        const db = event.target.result;
        
        // Store for cached visitors
        if (!db.objectStoreNames.contains(VISITORS_STORE)) {
          const visitorsStore = db.createObjectStore(VISITORS_STORE, { keyPath: 'qr_code' });
          visitorsStore.createIndex('id', 'id', { unique: true });
          visitorsStore.createIndex('property_id', 'property_id', { unique: false });
          visitorsStore.createIndex('cached_at', 'cached_at', { unique: false });
        }
        
        // Store for pending scans (offline validations)
        if (!db.objectStoreNames.contains(PENDING_SCANS_STORE)) {
          const pendingStore = db.createObjectStore(PENDING_SCANS_STORE, { keyPath: 'id', autoIncrement: true });
          pendingStore.createIndex('qr_code', 'qr_code', { unique: false });
          pendingStore.createIndex('scanned_at', 'scanned_at', { unique: false });
        }
      };
    });
  }

  async ensureDB() {
    if (!this.db) {
      await this.initPromise;
    }
    return this.db;
  }

  // Cache visitors for offline use
  async cacheVisitors(visitors) {
    const db = await this.ensureDB();
    const transaction = db.transaction([VISITORS_STORE], 'readwrite');
    const store = transaction.objectStore(VISITORS_STORE);
    
    const now = new Date().toISOString();
    
    for (const visitor of visitors) {
      const cachedVisitor = {
        ...visitor,
        cached_at: now
      };
      store.put(cachedVisitor);
    }
    
    return new Promise((resolve, reject) => {
      transaction.oncomplete = () => {
        log(`Cached ${visitors.length} visitors for offline use`);
        resolve();
      };
      transaction.onerror = () => reject(transaction.error);
    });
  }

  // Get cached visitor by QR code
  async getCachedVisitor(qrCode) {
    const db = await this.ensureDB();
    const transaction = db.transaction([VISITORS_STORE], 'readonly');
    const store = transaction.objectStore(VISITORS_STORE);
    
    return new Promise((resolve, reject) => {
      const request = store.get(qrCode);
      request.onsuccess = () => resolve(request.result);
      request.onerror = () => reject(request.error);
    });
  }

  // Get all cached visitors
  async getAllCachedVisitors() {
    const db = await this.ensureDB();
    const transaction = db.transaction([VISITORS_STORE], 'readonly');
    const store = transaction.objectStore(VISITORS_STORE);
    
    return new Promise((resolve, reject) => {
      const request = store.getAll();
      request.onsuccess = () => resolve(request.result || []);
      request.onerror = () => reject(request.error);
    });
  }

  // Get cached visitors count
  async getCachedVisitorsCount() {
    const db = await this.ensureDB();
    const transaction = db.transaction([VISITORS_STORE], 'readonly');
    const store = transaction.objectStore(VISITORS_STORE);
    
    return new Promise((resolve, reject) => {
      const request = store.count();
      request.onsuccess = () => resolve(request.result);
      request.onerror = () => reject(request.error);
    });
  }

  // Store a pending scan (validated offline)
  async storePendingScan(qrCode, scannedBy, visitorData, validationResult) {
    const db = await this.ensureDB();
    const transaction = db.transaction([PENDING_SCANS_STORE], 'readwrite');
    const store = transaction.objectStore(PENDING_SCANS_STORE);
    
    const pendingScan = {
      qr_code: qrCode,
      scanned_by: scannedBy,
      scanned_at: new Date().toISOString(),
      visitor_data: visitorData,
      validation_result: validationResult,
      synced: false
    };
    
    return new Promise((resolve, reject) => {
      const request = store.add(pendingScan);
      request.onsuccess = () => {
        log('Stored pending scan for later sync');
        resolve(request.result);
      };
      request.onerror = () => reject(request.error);
    });
  }

  // Get all pending scans
  async getPendingScans() {
    const db = await this.ensureDB();
    const transaction = db.transaction([PENDING_SCANS_STORE], 'readonly');
    const store = transaction.objectStore(PENDING_SCANS_STORE);
    
    return new Promise((resolve, reject) => {
      const request = store.getAll();
      request.onsuccess = () => {
        const scans = request.result || [];
        resolve(scans.filter(s => !s.synced));
      };
      request.onerror = () => reject(request.error);
    });
  }

  // Mark a pending scan as synced
  async markScanAsSynced(scanId) {
    const db = await this.ensureDB();
    const transaction = db.transaction([PENDING_SCANS_STORE], 'readwrite');
    const store = transaction.objectStore(PENDING_SCANS_STORE);
    
    return new Promise((resolve, reject) => {
      const getRequest = store.get(scanId);
      getRequest.onsuccess = () => {
        const scan = getRequest.result;
        if (scan) {
          scan.synced = true;
          const putRequest = store.put(scan);
          putRequest.onsuccess = () => resolve();
          putRequest.onerror = () => reject(putRequest.error);
        } else {
          resolve();
        }
      };
      getRequest.onerror = () => reject(getRequest.error);
    });
  }

  // Delete synced scans older than 24 hours
  async cleanupOldScans() {
    const db = await this.ensureDB();
    const transaction = db.transaction([PENDING_SCANS_STORE], 'readwrite');
    const store = transaction.objectStore(PENDING_SCANS_STORE);
    
    const cutoff = new Date(Date.now() - 24 * 60 * 60 * 1000).toISOString();
    
    return new Promise((resolve) => {
      const request = store.getAll();
      request.onsuccess = () => {
        const scans = request.result || [];
        scans.forEach(scan => {
          if (scan.synced && scan.scanned_at < cutoff) {
            store.delete(scan.id);
          }
        });
        resolve();
      };
    });
  }

  // Clean up old cached visitors (older than 7 days)
  async cleanupOldCache() {
    const db = await this.ensureDB();
    const transaction = db.transaction([VISITORS_STORE], 'readwrite');
    const store = transaction.objectStore(VISITORS_STORE);
    
    const cutoff = new Date(Date.now() - 7 * 24 * 60 * 60 * 1000).toISOString();
    
    return new Promise((resolve) => {
      const request = store.getAll();
      request.onsuccess = () => {
        const visitors = request.result || [];
        visitors.forEach(visitor => {
          if (visitor.cached_at < cutoff) {
            store.delete(visitor.qr_code);
          }
        });
        resolve();
      };
    });
  }

  // Validate QR offline
  validateOffline(visitor) {
    const now = new Date();
    
    // Check if visitor exists
    if (!visitor) {
      return {
        success: false,
        message: 'Código QR no encontrado en cache local',
        offline: true
      };
    }
    
    // Check expiration
    if (visitor.expiration_date) {
      const expDate = new Date(visitor.expiration_date);
      if (now > expDate) {
        return {
          success: false,
          message: 'QR expirado',
          visitor: visitor,
          offline: true
        };
      }
    }
    
    // Check status (only allow pending or multi-scan used)
    if (visitor.status === 'expired') {
      return {
        success: false,
        message: 'QR expirado',
        visitor: visitor,
        offline: true
      };
    }
    
    if (visitor.status === 'used' && !visitor.is_multi_scan) {
      return {
        success: false,
        message: 'QR ya fue utilizado (validación offline)',
        visitor: visitor,
        offline: true
      };
    }
    
    // Check start time for multi-scan
    if (visitor.start_time) {
      const startTime = new Date(visitor.start_time);
      if (now < startTime) {
        return {
          success: false,
          message: `QR válido a partir de ${startTime.toLocaleString()}`,
          visitor: visitor,
          offline: true
        };
      }
    }
    
    return {
      success: true,
      message: `Acceso permitido (OFFLINE) para ${visitor.name}`,
      visitor: visitor,
      offline: true
    };
  }

  // Sync pending scans with server
  async syncPendingScans() {
    if (!this.isOnline) return;
    
    const pendingScans = await this.getPendingScans();
    if (pendingScans.length === 0) return;
    
    log(`Syncing ${pendingScans.length} pending scans...`);
    
    const token = getToken();
    if (!token) return;
    
    const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
    
    for (const scan of pendingScans) {
      try {
        // Call the sync endpoint to record the offline scan
        // SEC-003 FIX: Don't send scanned_by, server uses authenticated user
        const response = await fetch(`${API}/visitors/sync-offline-scan`, {
          method: 'POST',
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json'
          },
          body: JSON.stringify({
            qr_code: scan.qr_code,
            scanned_at: scan.scanned_at
          })
        });
        
        // Only mark as synced if server responded successfully
        if (response.ok) {
          await this.markScanAsSynced(scan.id);
          log(`Synced scan for QR: ${scan.qr_code}`);
        } else if (response.status === 401) {
          // Token expired - stop syncing, user needs to re-login
          logError(`Token expired during sync, stopping`);
          break;
        } else {
          // Server error - leave pending for retry
          logError(`Server error syncing scan: ${response.status}`);
        }
      } catch (error) {
        // Network error - leave pending for retry
        logError(`Failed to sync scan: ${scan.qr_code}`, error);
      }
    }
    
    // Cleanup old synced scans
    await this.cleanupOldScans();
  }

  // Check online status
  getOnlineStatus() {
    return navigator.onLine;
  }
}

// Singleton instance
const offlineCacheService = new OfflineCacheService();

export default offlineCacheService;
